import React, { useState } from 'react';
import { useChartTooltip } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/useChartTooltip';
import { REPORTS_PAGE_COLORS } from 'pages/ReportsPage/components/AnalyticsDashboards/chartColors/chartColors.constants';
import {
  LINE_CHART_MARGIN,
  LINE_CHART_HEIGHT,
  LINE_CHART_DOT_RADIUS,
  LINE_CHART_DOT_HOVER_RADIUS,
  LINE_CHART_STROKE_WIDTH,
  formatDateLabel,
} from 'pages/ReportsPage/components/AnalyticsDashboards/DailyOvertimeDashboard/DailyOvertimeDashboard.constants';
import { useOtTrendChart } from 'pages/ReportsPage/components/AnalyticsDashboards/DailyOvertimeDashboard/useOtTrendChart';
import { EmptyStateContainer } from 'pages/ReportsPage/components/AnalyticsDashboards/DailyOvertimeDashboard/DailyOvertimeDashboard.styled';
import {
  ChartWrapper,
  LegendContainer,
  LegendDot,
  LegendLabel,
} from 'pages/ReportsPage/components/AnalyticsDashboards/DailyOvertimeDashboard/OtTrendChart.styled';

export const OtTrendChart: React.FC = () => {
  const {
    data,
    parsedData,
    containerRef,
    lineColor,
    chartWidth,
    innerWidth,
    innerHeight,
    xScale,
    yScale,
    linePath,
    xTicks,
    yTicks,
  } = useOtTrendChart();
  const tooltip = useChartTooltip();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  if (!data || data.length === 0) {
    return <EmptyStateContainer>No data available for the selected filters.</EmptyStateContainer>;
  }

  return (
    <ChartWrapper>
      <LegendContainer>
        <LegendDot $color={lineColor} />
        <LegendLabel>Overtime Hours</LegendLabel>
      </LegendContainer>
      <div ref={containerRef} style={{ width: '100%' }}>
        <svg
          width={chartWidth}
          height={LINE_CHART_HEIGHT}
          role="img"
          aria-label="Overtime trend chart showing overtime hours over time"
          tabIndex={0}
        >
          <g transform={`translate(${LINE_CHART_MARGIN.left},${LINE_CHART_MARGIN.top})`}>
            {yTicks.map((tick) => (
              <g key={`y-${tick}`}>
                <line
                  x1={0}
                  x2={innerWidth}
                  y1={yScale(tick)}
                  y2={yScale(tick)}
                  stroke={REPORTS_PAGE_COLORS.gridLine}
                  strokeDasharray="3,3"
                />
                <text
                  x={-8}
                  y={yScale(tick)}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fontSize={11}
                  fill={REPORTS_PAGE_COLORS.axisLabel}
                >
                  {tick}
                </text>
              </g>
            ))}

            {xTicks.map((tick) => (
              <text
                key={`x-${tick.getTime()}`}
                x={xScale(tick)}
                y={innerHeight + 28}
                textAnchor="middle"
                fontSize={11}
                fill={REPORTS_PAGE_COLORS.axisLabel}
              >
                {formatDateLabel(tick)}
              </text>
            ))}

            <path
              d={linePath}
              fill="none"
              stroke={lineColor}
              strokeWidth={LINE_CHART_STROKE_WIDTH}
            />

            {parsedData.map((d, i) => (
              <circle
                key={`dot-${d.raw.date}`}
                cx={xScale(d.date)}
                cy={yScale(d.value)}
                r={hoveredIndex === i ? LINE_CHART_DOT_HOVER_RADIUS : LINE_CHART_DOT_RADIUS}
                fill={lineColor}
                stroke="#fff"
                strokeWidth={2}
                style={{ cursor: 'pointer', transition: 'r 0.15s' }}
                tabIndex={0}
                aria-label={`${formatDateLabel(d.date)}: ${d.value} overtime hours`}
                onMouseEnter={(e) => {
                  setHoveredIndex(i);
                  tooltip.show(
                    e,
                    `<strong>${formatDateLabel(d.date)}</strong><br/>Overtime: ${d.value}h`,
                  );
                }}
                onMouseMove={(e) => tooltip.move(e)}
                onMouseLeave={() => {
                  setHoveredIndex(null);
                  tooltip.hide();
                }}
                onFocus={(e) => {
                  setHoveredIndex(i);
                  tooltip.show(
                    e,
                    `<strong>${formatDateLabel(d.date)}</strong><br/>Overtime: ${d.value}h`,
                  );
                }}
                onBlur={() => {
                  setHoveredIndex(null);
                  tooltip.hide();
                }}
              />
            ))}
          </g>
        </svg>
      </div>
    </ChartWrapper>
  );
};
